import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Zap, Shield, Minus, Plus } from 'lucide-react';

interface OCOControlsProps {
  useOCO: boolean;
  setUseOCO: (value: boolean) => void;
  ocoTPEnabled: boolean;
  setOcoTPEnabled: (value: boolean) => void;
  ocoSLEnabled: boolean;
  setOcoSLEnabled: (value: boolean) => void;
  ocoTPOffset: number;
  ocoSLOffset: number;
  handleTPChange: (value: number) => void;
  handleSLChange: (value: number) => void;
  isHedgeCopyModeActive: boolean;
}

export const OCOControls: React.FC<OCOControlsProps> = ({
  useOCO,
  setUseOCO,
  ocoTPEnabled,
  setOcoTPEnabled,
  ocoSLEnabled,
  setOcoSLEnabled,
  ocoTPOffset,
  ocoSLOffset,
  handleTPChange,
  handleSLChange,
  isHedgeCopyModeActive,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  // Calculate popover position
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 6,
        left: rect.left,
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const buttonClass = `trading-oco-btn ${useOCO ? 'on' : ''} ${isHedgeCopyModeActive ? 'hedge' : ''}`;

  return (
    <>
      <button
        ref={buttonRef}
        className={buttonClass}
        onClick={() => setIsOpen(!isOpen)}
        title="OCO (One Cancels Other) Settings"
      >
        {isHedgeCopyModeActive ? <Shield size={10} /> : <Zap size={10} />}
        <span>{isHedgeCopyModeActive ? 'HEDGE' : 'OCO'}</span>
        {useOCO && (
          <span className="oco-values">
            {isHedgeCopyModeActive ? ocoTPOffset :
              `${ocoTPEnabled ? ocoTPOffset : '-'}/${ocoSLEnabled ? ocoSLOffset : '-'}`}
          </span>
        )}
      </button>

      {isOpen && createPortal(
        <>
          <div className="trading-popover-overlay" onClick={() => setIsOpen(false)} />
          <div
            ref={popoverRef}
            className="trading-oco-popover"
            style={{
              top: popoverPosition.top,
              left: popoverPosition.left,
            }}
          >
            <div className="trading-oco-popover-header">
              <span className="trading-oco-popover-title">
                {isHedgeCopyModeActive ? 'Hedge Mode' : 'OCO Settings'}
              </span>
              <button
                className={`trading-oco-toggle ${useOCO ? 'on' : ''}`}
                onClick={() => setUseOCO(!useOCO)}
              >
                <div className="trading-oco-toggle-switch">
                  <div className="trading-oco-toggle-knob" />
                </div>
                <span className="trading-oco-toggle-label">{useOCO ? 'ON' : 'OFF'}</span>
              </button>
            </div>

            {isHedgeCopyModeActive && (
              <div className="trading-hedge-banner">
                <Shield size={10} />
                <span>HEDGE TP=SL enforced</span>
              </div>
            )}

            {useOCO && (
              <div className="trading-oco-inputs">
                {isHedgeCopyModeActive ? (
                  <div className="trading-oco-input-row full-width">
                    <span className="trading-oco-label hedge">TP/SL</span>
                    <div className="trading-oco-input-wrapper">
                      <button
                        className="trading-oco-input-btn"
                        onClick={() => handleTPChange(Math.max(0.25, ocoTPOffset - 0.25))}
                      >
                        <Minus size={10} />
                      </button>
                      <input
                        type="number"
                        className="trading-oco-input"
                        value={ocoTPOffset}
                        onChange={(e) => handleTPChange(Number(e.target.value))}
                        step="0.25"
                        min="0.25"
                      />
                      <button
                        className="trading-oco-input-btn"
                        onClick={() => handleTPChange(ocoTPOffset + 0.25)}
                      >
                        <Plus size={10} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`trading-oco-input-row ${!ocoTPEnabled ? 'disabled' : ''}`}>
                      <label className="trading-oco-checkbox-label">
                        <input
                          type="checkbox"
                          checked={ocoTPEnabled}
                          onChange={(e) => setOcoTPEnabled(e.target.checked)}
                          className="trading-oco-checkbox"
                        />
                        <span className="trading-oco-label">TP</span>
                      </label>
                      <div className="trading-oco-input-wrapper">
                        <button
                          className="trading-oco-input-btn"
                          onClick={() => handleTPChange(Math.max(0.25, ocoTPOffset - 0.25))}
                          disabled={!ocoTPEnabled}
                        >
                          <Minus size={10} />
                        </button>
                        <input
                          type="number"
                          className="trading-oco-input"
                          value={ocoTPOffset}
                          onChange={(e) => handleTPChange(Number(e.target.value))}
                          step="0.25"
                          min="0.25"
                          disabled={!ocoTPEnabled}
                        />
                        <button
                          className="trading-oco-input-btn"
                          onClick={() => handleTPChange(ocoTPOffset + 0.25)}
                          disabled={!ocoTPEnabled}
                        >
                          <Plus size={10} />
                        </button>
                      </div>
                    </div>
                    <div className={`trading-oco-input-row ${!ocoSLEnabled ? 'disabled' : ''}`}>
                      <label className="trading-oco-checkbox-label">
                        <input
                          type="checkbox"
                          checked={ocoSLEnabled}
                          onChange={(e) => setOcoSLEnabled(e.target.checked)}
                          className="trading-oco-checkbox"
                        />
                        <span className="trading-oco-label sl">SL</span>
                      </label>
                      <div className="trading-oco-input-wrapper">
                        <button
                          className="trading-oco-input-btn"
                          onClick={() => handleSLChange(Math.max(0.25, ocoSLOffset - 0.25))}
                          disabled={!ocoSLEnabled}
                        >
                          <Minus size={10} />
                        </button>
                        <input
                          type="number"
                          className="trading-oco-input"
                          value={ocoSLOffset}
                          onChange={(e) => handleSLChange(Number(e.target.value))}
                          step="0.25"
                          min="0.25"
                          disabled={!ocoSLEnabled}
                        />
                        <button
                          className="trading-oco-input-btn"
                          onClick={() => handleSLChange(ocoSLOffset + 0.25)}
                          disabled={!ocoSLEnabled}
                        >
                          <Plus size={10} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </>,
        document.body
      )}
    </>
  );
};
